import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { ReactComponent as DownIcon } from "../../../assets/Icon-chevron-down.svg";
import { useEffect, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import { FilterObj } from "../../../types";
import CheckIcon from "@mui/icons-material/Check";
import {
  formatDate,
  addDate,
  subtractMonths,
  subtractDays,
  HandleDataLayer,
} from "../../../Helpers";
import { DateRangeSelectorModal } from "./DateRangeSelectorModal";

const useStyles = makeStyles((theme) => ({
  menuPaper: {
    boxShadow: "0px 8px 12px #00000019",
    border: "1px solid #E8E8E8",
    borderRadius: 8,
  },
  menuList: {
    width: 193,
    padding: "12px",
    "& li": {
      color: "#747474",
      fontWeight: "500",
      padding: "8px 12px",
    },
  },
  button: {
    padding: "16px",
    backgroundColor: "transparent",
    borderRadius: "8px",
    color: theme.palette.text.secondary,
    height: "45px",
    boxShadow: "none",
    fontSize: "16px",
    "&:hover": {
      backgroundColor: "#F2F2F2",
      height: "45px",
      textDecoration: "underline",
      color: theme.palette.text.primary,
      boxShadow: "none",
      transform: "none",
    },
  },
  chevronIconDown: {
    marginLeft: "8px",
    fontSize: "16px",
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.text.primary,
      boxShadow: "none",
      transform: "none",
    },
  },
  chevronIconUp: {
    marginLeft: "8px",
    fontSize: "16px",
    color: theme.palette.text.secondary,
    transform: "rotate(180deg)",
    "&:hover": {
      color: theme.palette.text.primary,
      boxShadow: "none",
      transform: "none",
    },
  },
  selectedMenuItemText: {
    font: "normal normal bold 16px/20px Roboto !important",
    color: "#191919 !important",
  },
  menuItemText: {
    font: "normal normal 500 16px/20px Roboto !important",
  },
}));

interface DateFilterDropdownProps {
  selectedFilter?: FilterObj;
  onFilterChange: (filter: FilterObj) => void;
  section: string; // for data layer
}

interface menuItemProps {
  selected: boolean;

  onClick: () => void;
  filter: FilterObj;
}

const FilterItem = (props: menuItemProps) => {
  const { selected, onClick, filter } = props;
  const classes = useStyles();
  const [hover, setHover] = useState(false);
  return (
    <MenuItem
      selected={selected}
      key={filter.id}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}>
      <ListItemIcon>
        {selected ? <CheckIcon fontSize="small" color={"primary"} /> : <></>}
      </ListItemIcon>
      <ListItemText>
        <Typography
          component={"span"}
          variant={"body1"}
          className={
            hover || selected ? classes.selectedMenuItemText : classes.menuItemText
          }>
          {filter.id === "custom" ? "Custom dates" : filter.label}
        </Typography>
      </ListItemText>
    </MenuItem>
  );
};

export const AVAILABLE_DATE_FILTERS = [
  {
    label: "Today",
    id: "last_1_day",
    endDate: formatDate(addDate(new Date(), 1)),
    startDate: formatDate(subtractDays(Date.now(), 1)),
  },
  {
    label: "Last 7 days",
    id: "last_7_days",
    endDate: formatDate(addDate(new Date(), 1)),
    startDate: formatDate(subtractDays(Date.now(), 7)),
  },
  {
    label: "Last 30 days",
    id: "last_30_days",
    endDate: formatDate(addDate(new Date(), 1)),
    startDate: formatDate(subtractDays(Date.now(), 30)),
  },
  {
    label: "Last 12 months",
    id: "last_12_months",
    endDate: formatDate(addDate(new Date(), 1)),
    startDate: formatDate(subtractMonths(new Date(), 12)),
  },
  {
    label: "All time",
    id: "all_time",
    endDate: formatDate(addDate(new Date(), 1)),
    startDate: formatDate(subtractMonths(new Date(), 1200)),
  },
  {
    label: "Custom dates",
    id: "custom",
    endDate: formatDate(addDate(new Date(), 1)),
    startDate: formatDate(subtractDays(Date.now(), 30)),
  },
];

export const DateFilterDropdown = (props: DateFilterDropdownProps) => {
  const classes = useStyles();
  const { selectedFilter, onFilterChange, section } = props;
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [mainMenuOpen, setMainMenuOpen] = useState(false);
  const [availableFilters, setAvailableFilters] = useState(AVAILABLE_DATE_FILTERS);
  const [activeFilter, setActiveFilter] = useState(selectedFilter ?? availableFilters[4]);
  const [openDateRangeSelector, setOpenDateRangeSelector] = useState(false);

  useEffect(() => {
    if (activeFilter.id === "custom") {
      setOpenDateRangeSelector(true);
      return;
    }
    onFilterChange(activeFilter);
  }, [activeFilter, onFilterChange]);
  return (
    <>
      <Box>
        <Button
          className={classes.button}
          variant="contained"
          disableElevation
          ref={buttonRef}
          onClick={() => {
            HandleDataLayer(
              "collapse",
              "open",
              "click",
              "main content",
              section,
              activeFilter.label,
              "onclick"
            );
            setMainMenuOpen(true);
          }}>
          <i className="fa-solid fa-calendar-days" style={{ marginRight: "8px" }}></i>
          {activeFilter.label}
          <DownIcon
            className={mainMenuOpen ? classes.chevronIconUp : classes.chevronIconDown}
          />
        </Button>
        <Menu
          anchorEl={buttonRef.current}
          open={mainMenuOpen}
          onClose={() => {
            HandleDataLayer(
              "collapse",
              "close",
              "click",
              "main content",
              section,
              activeFilter.label,
              "onclick"
            );
            setMainMenuOpen(false);
          }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          transformOrigin={{ horizontal: "right", vertical: -8 }}
          MenuListProps={{ className: classes.menuList }}
          PaperProps={{ className: classes.menuPaper }}>
          {availableFilters
            .filter((availableFilter) => availableFilter.id !== "custom-date-range")
            .map((filter) => {
              let selected = activeFilter.label === filter.label;
              return (
                <FilterItem
                  key={filter.id}
                  filter={filter}
                  onClick={() => {
                    setActiveFilter(filter);
                    HandleDataLayer(
                      filter.id === "custom" ? "modal" : "link",
                      filter.id === "custom" ? "open" : "click",
                      filter.id === "custom" ? "click" : "internal link",
                      "main content",
                      "date dropdown",
                      filter.id === "custom" ? "custom dates" : filter.label,
                      "onclick"
                    );
                    setMainMenuOpen(false);
                  }}
                  selected={selected}
                />
              );
            })}
        </Menu>
      </Box>
      <DateRangeSelectorModal
        open={openDateRangeSelector}
        onClose={() => {
          setActiveFilter(availableFilters[1]);
          setOpenDateRangeSelector(false);
        }}
        endDate={new Date()}
        startDate={new Date()}
        onDateRangeChange={(startDate, endDate) => {
          setOpenDateRangeSelector(false);
          let filter = {} as FilterObj;
          filter.label = `${formatDate(startDate!)} - ${formatDate(endDate!)}`;
          filter.id = "custom-date-range";
          filter.startDate = formatDate(startDate!);
          filter.endDate = formatDate(endDate!);
          setAvailableFilters((prev) => [...prev, filter]);
          setActiveFilter(filter);
        }}
      />
    </>
  );
};
